import { toast } from '@mx/vix';
import { normalizeError, delay } from '@mx/pay-common';
import signHelper from '@mx/payment-sign-sdk';
import {
  bridge,
  checkPayToken,
  CheckResult,
  PayType,
  env,
  getStorage,
  monitor,
  NextStep,
  PAY_TOKEN,
  qualifyDispatcher,
  removeStorage,
  metricPoints,
  SignStatusType,
  ToastType,
} from '@/common';
import { showErrorModal } from './errorModal';
import {
  cancelDefer,
  delayPayCashierPaySuccess,
  delayPaydowngradeStandardCashier,
  cancelDelayPayCashier,
} from './callbackNativeRouter';
import { goDirectPay } from './wxScorePaySign';
import { pollingQueryOrder } from './queryOrder';
import type { PaydelayCashierpayPayLoad } from '@/store/constants';
import { storeInstance } from '@/store';

const store = storeInstance();
function checkToken() {
  return checkPayToken({
    bizSerialNo: env.bizSerialNo,
    validateToken: env.validateToken,
  })
    .then((res) => {
      if (res.checkResult === CheckResult.Success) {
        fetchQualifyDispatcher();
      } else {
        throw new Error('[checktoken] payToken验证不通过失败');
      }
    })
    .catch((err) => {
      throw err;
    });
}

function appear() {
  bridge.subscribe({
    action: 'appear',
    handle: () => {
      getStorage(PAY_TOKEN)
        .then((value: any) => {
          env.setValidateToken(value);
        })
        .then(() => checkToken())
        .catch((error) => {
          monitor.captureInfo({
            name: error.message || '[storage]获取 payToken 失败',
            msg: {
              error: normalizeError(error),
            },
          });
          // 失败时需要隐藏 fetchQualifyDispatcher 函数设置的 toast，否则用户无法继续点击按钮
          toast.hide();
          // 自动开通流程不验证直接返回，需要关闭透明的webview
          if (env.hideHalfPage) {
            cancelDefer();
          }
        })
        .finally(() => {
          removeStorage(PAY_TOKEN);
        });

      bridge.unsubscribe({
        action: 'appear',
      });
    },
  });
}

export function fetchQualifyDispatcher() {
  toast.show('loading', '', {
    delay: 0,
  });

  // 签约支付请求参数
  const qualifydispatcherParams: QualifyDispatcherParamsType = {
    scorePlanId: env.coreBusinessParams.scorePlanId,
    openType: env.performanceOpenType,
    bizSerialNo: env.bizSerialNo,
    utmSource: env.utmSource,
    freePassword: env.freePassword,
  };

  qualifyDispatcher({
    ...env.inpayBasicBusinessParams,
    ...qualifydispatcherParams,
  })
    .then((res) => {
      // 目前只支持独立验证流程，其他流程抛错上报
      env.setBizSerialNo(res.bizSerialNo);
      if (res.finalStepFlag) {
        const { retryCount, retryInterval, payFailAction } = res;
        env.setPayFailAction(payFailAction ?? '');
        // 轮询支付结果
        pollingQueryOrder(retryCount ?? 8, retryInterval ?? 1000);
      } else if (res.nextStep === NextStep.PassVerify) {
        appear();
        if (!res.jumpUrl) {
          monitor.captureAjaxErrorMessage('[api]后端未下发验证密码地址', { res });
        }
        bridge.openPage(res.jumpUrl ?? '');
      } else {
        throw new Error('nextStep 暂不支持');
      }
    })
    .catch((error) => {
      toast.hide();
      showErrorModal({ resultData: error });
      monitor.captureAjaxErrorMessage('[api]qualifyDispatcher引导接口请求失败', { error: normalizeError(error) });
    });
}

export function payDelayToPay() {
  monitor.reportMetric(metricPoints.clickPayButton);
  if (env.payType === PayType.PayDelay) {
    const needOpenDelaypay = store.state.paydelaycashier.displayConsultData?.extendInfo?.need_open_delaypay;
    if (needOpenDelaypay) {
      toast.show('loading', '', {
        delay: 0,
      });
      const loadingHide = () => {
        toast.hide();
      };
      const successCallBack = () => {
        monitor.reportMetric(metricPoints.signedStatus, { signStatus: SignStatusType.Success, openType: env.openType });
        loadingHide();
        store
          .dispatch<PaydelayCashierpayPayLoad>(
            {
              type: 'paydelaycashier/pay',
              delayPayCashierPaySuccess,
              delayPaydowngradeStandardCashier,
              cancelDelayPayCashier,
            },
            { root: true }
          )
          .catch((error) => {
            monitor.captureJSErrorMessage('先用后付支付报错', {
              error: normalizeError(error),
            });
          });
      };
      const failCallBack = () => {
        monitor.reportMetric(metricPoints.signedStatus, { signStatus: SignStatusType.Fail, openType: env.openType });
        toast.hide();
        store.commit({
          type: 'paydelaycashier/setToast',
          show: true,
          toastType: ToastType.Text,
          toastText: '签约结果失败/未查到，请稍后再试',
        });
        // eslint-disable-next-line promise/catch-or-return
        delay(2000).then(() => {
          store.commit({ type: 'paydelaycashier/setToast', show: false });
          delayPaydowngradeStandardCashier();
        });
      };
      monitor.reportMetric(metricPoints.signedStatus, { signStatus: SignStatusType.Start, openType: env.openType });
      const signHelperParams = {
        source: 'pay_delay_sign_halfpage',
        scorePlanId: env.coreBusinessParams.scorePlanId,
        utmSource: env.utmSource,
        openType: 1,
        successCallBack, // 签约成功回调函数
        failCallBack, // 签约失败回调函数
        loadingHide, // 隐藏loading
        // 独立签约需要的营销参数
        extParams: {
          lastPromos: store.state.paydelaycashier?.promoInfo?.lastPromos || '',
        },
      };
      signHelper.init({ ...signHelperParams });
      signHelper.fetchQualifyDispatcher();
    } else {
      store
        .dispatch<PaydelayCashierpayPayLoad>(
          {
            type: 'paydelaycashier/pay',
            delayPayCashierPaySuccess,
            delayPaydowngradeStandardCashier,
            cancelDelayPayCashier,
          },
          { root: true }
        )
        .catch((error) => {
          monitor.captureJSErrorMessage('先用后付支付报错', {
            error: normalizeError(error),
          });
        });
    }
  } else if (env.payType === PayType.WeiXin) {
    goDirectPay();
  } else {
    monitor.captureInfo({
      name: '[biz]先用后付支付非预期内的流程',
      msg: { payType: env.payType },
    });
    toast.show(
      'text',
      '<div class="toast-warning"><div class="error-icon"><i class="icon-warning-outline"></i></div><p class="toast-error-text">先用后付暂不可用</p><p class="toast-error-subText">请稍后重试</p></div>',
      {
        duration: 2000,
      }
    );
    // eslint-disable-next-line promise/catch-or-return
    delay(2000).then(() => {
      delayPaydowngradeStandardCashier();
    });
  }
}
